import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

function TrackingBanner({ user, onViewTracking, hasCart }) {
  const [activeOrders, setActiveOrders] = useState([]);
  const [currentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (!user?.uid) return () => {};

    const ordersQuery = query(
      collection(db, 'orders'),
      where('userId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(ordersQuery, (snapshot) => {
      const orders = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((order) => 
          order.status && 
          order.status !== 'delivered' && 
          order.status !== 'cancelled'
        )
        .sort((a, b) => (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0));
      
      setActiveOrders(orders);
      if (orders.length === 0) {
        setIsVisible(false);
      }
    });

    return () => unsubscribe();
  }, [user]);



  if (!isVisible || activeOrders.length === 0) return null;

  const order = activeOrders[currentIndex];
  const statusEmoji = {
    pending: '⏳',
    confirmed: '✅',
    shipped: '📦',
    'out-for-delivery': '🚚'
  };

  const statusColor = {
    pending: '#f59e0b',
    confirmed: '#16a34a',
    shipped: '#3b82f6',
    'out-for-delivery': '#0ea5e9'
  };

  const isActiveOrder = order.status !== 'delivered' && order.status !== 'cancelled';

  return (
    <div
      style={{
        margin: '10px 14px 8px',
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderRadius: '12px',
        padding: '8px 12px',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.06)',
        minHeight: '54px',
        animation: 'slideUpSoft 0.4s ease-out',
      }}
    >
      <style>{`
        @keyframes slideUpSoft {
          from { transform: translateY(10px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes pulseSoft {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.12); opacity: 0.85; }
        }
      `}</style>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
          <span style={{ 
            fontSize: '18px',
            flexShrink: 0,
            animation: isActiveOrder ? 'pulseSoft 2.2s ease-in-out infinite' : 'none'
          }}>
            {statusEmoji[order.status] || '📦'}
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.1', minWidth: 0 }}>
            <span style={{ 
              fontSize: '14px', 
              fontWeight: '600', 
              textTransform: 'capitalize', 
              color: statusColor[order.status] || '#3b82f6',
              marginBottom: '3px'
            }}>
              {order.status === 'out-for-delivery' ? 'Out for Delivery' : order.status}
            </span>
            <span style={{ 
              fontSize: '12px', 
              color: '#6b7280',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '200px'
            }}>
              Order #{order.id.substring(0, 8).toUpperCase()} • {order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewTracking(order.id);
            }}
            style={{
              background: '#ff6b35',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '0 14px',
              height: '36px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 10px rgba(255, 107, 53, 0.25)',
              transition: 'transform 0.12s ease'
            }}
            onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.96)'}
            onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            onTouchStart={(e) => e.currentTarget.style.transform = 'scale(0.96)'}
            onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            View
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsVisible(false);
            }}
            style={{
              background: '#f3f4f6',
              border: 'none',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#6b7280',
              fontSize: '18px',
              fontWeight: '400',
              opacity: 0.6,
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}

export default TrackingBanner;
